import { useEffect, useCallback, useState, useRef } from "react";
import { trackEvent } from "helpers/analyticsHelpers";
import SpeedTestWorker from "../helpers/speedtest.worker?worker";
import { createChannelSetting } from "helpers/resolution";
import { type ChannelSetting } from "views/page-content/platforms/platform/platform-helper";
import { QualitySelectionLabel } from "store/platforms/types";

export enum SpeedTestStatus {
    Waiting = "waiting",
    InProgress = "inprogress",
    Completed = "completed",
    Error = "error"
}

export interface SpeedTestResults {
    upload?: number;
    suggestedSetting?: { name: QualitySelectionLabel; setting: ChannelSetting };
    error?: {
        message: string;
    };
}

export interface UseSpeedTestOptions {
    onInProgress?: () => void;
    onCompleted?: (results: SpeedTestResults) => void;
    onError?: (error: { message: string }) => void;
    runImmediately?: boolean;
    hasFullHD?: boolean;
}

export function useSpeedTest({
    onInProgress,
    onCompleted,
    onError,
    runImmediately = true,
    hasFullHD = true
}: UseSpeedTestOptions = {}) {
    const worker = useRef<Worker | null>(null);

    const [speedTestStatus, setSpeedTestStatus] = useState<SpeedTestStatus>(
        SpeedTestStatus.Waiting
    );
    const [results, setResults] = useState<SpeedTestResults | undefined>(
        undefined
    );

    const _onInProgress = useCallback(() => {
        setSpeedTestStatus(SpeedTestStatus.InProgress);
        onInProgress?.();
    }, [onInProgress]);

    const _onCompleted = useCallback(
        (results: SpeedTestResults) => {
            setResults(results);
            setSpeedTestStatus(SpeedTestStatus.Completed);
            onCompleted?.(results);

            trackEvent("Speed test completed", {
                testResult: results,
                source: "cloudflare"
            });
        },
        [onCompleted]
    );

    const _onError = useCallback(
        (error: { message: string }) => {
            setResults({ error });
            setSpeedTestStatus(SpeedTestStatus.Error);
            onError?.(error);

            // Do we want to report/track speed test errors or just relay to user?
        },
        [onError]
    );

    useEffect(() => {
        if (worker.current) return;

        worker.current = new SpeedTestWorker();

        return () => {
            if (worker.current) {
                worker.current.terminate();
                worker.current = null;
            }
        };
    }, []);

    useEffect(() => {
        worker.current.onmessage = (event) => {
            const { type, results, error } = event.data;

            if (type === "finished") {
                const setting = createChannelSetting(results.upload, hasFullHD);

                _onCompleted({
                    ...results,
                    suggestedSetting: {
                        name: `${setting["video-frame-height"]}p`,
                        setting
                    }
                });
            } else if (type === "error") {
                _onError(error);
            } else if (type === "in-progress") {
                _onInProgress();
            }
        };
    }, [_onCompleted, _onError, _onInProgress, hasFullHD]);

    const _run = useCallback(() => {
        if (speedTestStatus !== SpeedTestStatus.Waiting) return;

        worker.current.postMessage({ type: "start" });
    }, [speedTestStatus]);

    useEffect(() => {
        if (runImmediately) {
            _run();
        }
    }, [_run, runImmediately]);

    const runSpeedTest = useCallback(() => {
        if (worker.current) {
            setSpeedTestStatus(SpeedTestStatus.Waiting);
            worker.current.postMessage({ type: "start" });
        }
    }, []);

    return {
        speedTestStatus,
        results,
        runSpeedTest
    };
}
